import { MgItemInfo } from '@retail/my-assortment/components';
import { BodyLoader, SideDrawerPaper } from '@retail/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import {
  PriceControlAction,
  PriceControlItem,
  PriceControlItemEvents,
} from '@retail/price-control/types';
import { Stack } from '@mui/material';
import { PriceControlItemAction } from '@retail/price-control/components';
import { useCallback, useMemo, useState } from 'react';
import { ContainedButton } from '@shared/components';
import {
  useFetchPriceControlItemEvents,
  usePriceControlItemEventAction,
} from '@retail/price-control/data-access';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  assortmentId: number;
  priceControlItem: PriceControlItem;
  includeVat: boolean;
  onClose: () => void;
}

export function HandleItemContainer({
  assortmentId,
  priceControlItem,
  includeVat,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: priceControlItemEventsDto, isLoading } = useFetchPriceControlItemEvents({
    assortmentId,
    memberEventId: priceControlItem.memberEventId,
  });
  const priceControlItemEvents = useMemo(
    () => PriceControlItemEvents.fromDto(priceControlItemEventsDto!),
    [priceControlItemEventsDto]
  );

  const [priceControlAction, setPriceControlAction] = useState<PriceControlAction | undefined>();
  const handleSetPriceControlAction = useCallback(
    (action: PriceControlAction) =>
      priceControlAction === action
        ? setPriceControlAction(undefined)
        : setPriceControlAction(action),
    [priceControlAction]
  );

  const { mutateAsync: postItemEventAction } = usePriceControlItemEventAction({ assortmentId });
  const handleItemEventAction = useCallback(() => {
    postItemEventAction({
      body: [
        {
          priceControlMemberEventId: priceControlItem.memberEventId,
          action: priceControlAction!,
        },
      ],
    })
      .then(() => {
        toast.success(t('myAssortment.priceControl.itemHandled'));
        queryClient.invalidateQueries({ queryKey: ['price-control-supplier-aggregated'] });
        onClose();
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('myAssortment.actions.genericActionError'));
      });
  }, [
    onClose,
    postItemEventAction,
    priceControlAction,
    priceControlItem.memberEventId,
    queryClient,
    t,
    toast,
  ]);

  const oldSalesPrice = includeVat
    ? priceControlItem.pricesIncVat?.salesPriceBefore
    : priceControlItem.pricesExVat?.salesPriceBefore;

  return (
    <SideDrawerPaper>
      <MgItemInfo
        primaryText={priceControlItem.primaryText}
        secondaryText={priceControlItem.secondaryText}
        mgItemNumber={priceControlItem.mgItemNumber}
        closeProductCard={onClose}
      />
      <Stack px={3} py={1} gap={2}>
        {isLoading ? (
          <BodyLoader />
        ) : (
          <>
            <PriceControlItemAction
              action="DO_NOTHING"
              oldSalesPrice={oldSalesPrice}
              newValues={priceControlItemEvents.doNothingResult}
              isChecked={priceControlAction === 'DO_NOTHING'}
              onCheck={() => handleSetPriceControlAction('DO_NOTHING')}
              disabled={false}
              includeVat={includeVat}
            />
            <PriceControlItemAction
              action="KEEP_OLD_COVERAGE"
              oldSalesPrice={oldSalesPrice}
              newValues={priceControlItemEvents.keepOldCoverageResult}
              isChecked={priceControlAction === 'KEEP_OLD_COVERAGE'}
              onCheck={() => handleSetPriceControlAction('KEEP_OLD_COVERAGE')}
              disabled={!priceControlItem.validActions.includes('KEEP_OLD_COVERAGE')}
              includeVat={includeVat}
            />
            <PriceControlItemAction
              action="KEEP_OLD_PRICE"
              oldFactorAndDG={{
                factor: priceControlItem.salesFactorBefore,
                coverageDegree: priceControlItem.coverageDegreeBefore,
              }}
              newValues={priceControlItemEvents.keepOldPriceResult}
              isChecked={priceControlAction === 'KEEP_OLD_PRICE'}
              onCheck={() => handleSetPriceControlAction('KEEP_OLD_PRICE')}
              disabled={!priceControlItem.validActions.includes('KEEP_OLD_PRICE')}
              includeVat={includeVat}
            />
            <PriceControlItemAction
              action="FREEZE_OLD_PRICE"
              oldFactorAndDG={{
                factor: priceControlItem.salesFactorBefore,
                coverageDegree: priceControlItem.coverageDegreeBefore,
              }}
              newValues={priceControlItemEvents.freezeOldPriceResult}
              isChecked={priceControlAction === 'FREEZE_OLD_PRICE'}
              onCheck={() => handleSetPriceControlAction('FREEZE_OLD_PRICE')}
              disabled={!priceControlItem.validActions.includes('FREEZE_OLD_PRICE')}
              includeVat={includeVat}
            />
            <PriceControlItemAction
              action="SPLIT_DIFFERENCE"
              oldSalesPrice={oldSalesPrice}
              oldFactorAndDG={{
                coverageDegree: priceControlItem.coverageDegreeBefore,
                factor: priceControlItem.salesFactorBefore,
              }}
              newValues={priceControlItemEvents.splitDifferenceResult}
              isChecked={priceControlAction === 'SPLIT_DIFFERENCE'}
              onCheck={() => handleSetPriceControlAction('SPLIT_DIFFERENCE')}
              disabled={false}
              includeVat={includeVat}
            />
          </>
        )}

        <ContainedButton
          sx={{ alignSelf: 'flex-end' }}
          size="small"
          disabled={!priceControlAction}
          onClick={handleItemEventAction}
        >
          {t('myAssortment.save')}
        </ContainedButton>
      </Stack>
    </SideDrawerPaper>
  );
}
